/**
 * Normalize schedule REST payloads into the in-memory shapes the page uses: jobs for the
 * left column and date clamping, groups for the tree, blocks and nested aspects for bars
 * and per-day crew rows (see also {@link useScheduleController}).
 */

import { toLocalCalendarDay } from "../../../../utils/datetime/core";

/**
 * Parse API date-only strings into noon-local calendar days for stable math.
 *
 * @param {string|Date|null|undefined} value ISO date or `YYYY-MM-DD`.
 * @returns {Date}
 */
function parseApiDateOnly(value) {
  let d;
  if (value == null) {
    d = new Date();
  } else if (value instanceof Date) {
    d = value;
  } else {
    const part = String(value).split("T")[0];
    const [y, m, day] = part.split("-").map((n) => parseInt(n, 10));
    if (!y || !m || !day) {
      d = new Date();
    } else {
      d = new Date(y, m - 1, day);
    }
  }
  return toLocalCalendarDay(d);
}

/**
 * Normalize a JobNumber payload into the project row shape consumed by schedule UI/state.
 *
 * @param {Object} job
 * @returns {{ id: unknown, projectNumber: string, name: string, date_start: string|null, date_end: string|null }|null}
 */
export function mapJobNumberToProject(job) {
  if (!job) {
    return null;
  }
  return {
    id: job.id,
    projectNumber: job.number ?? job.project_number ?? "",
    name: job.description ?? "",
    date_start: job.date_start ?? null,
    date_end: job.date_end ?? null,
  };
}

/**
 * Normalize a schedule-group API row to the local tree/group model.
 *
 * @param {Object} g
 * @returns {{ id: unknown, projectId: unknown, parentGroupId: unknown, name: string, color: string, order: number }|null}
 */
export function mapScheduleGroupFromApi(g) {
  if (!g) {
    return null;
  }
  return {
    id: g.id,
    projectId: g.job_id,
    parentGroupId: g.parent_id ?? null,
    name: g.name ?? "",
    color: g.color ?? "",
    order: g.sort_order ?? 0,
  };
}

/**
 * Normalize a schedule block API row into in-memory block shape.
 *
 * `contains_locked` is attached later from `DayDetails.locked` via
 * {@link mergeBlocksContainsLocked} (date span vs `scheduleDayLocks`).
 *
 * @param {Object} b
 * @returns {Object|null}
 */
export function mapBlockFromApi(b) {
  if (!b) {
    return null;
  }
  const job = b.job ?? {};
  const ws = b.work_scope ?? {};
  const sg = b.schedule_group;
  const blockWorkers = Array.isArray(b.worker_assignment)
    ? b.worker_assignment
    : [];
  return {
    id: b.id,
    job: {
      id: job.id,
      number: job.number ?? job.project_number ?? "",
      description: job.description ?? "",
    },
    scheduleGroup: sg != null ? { id: sg.id } : null,
    scope: {
      id: ws.id,
      name: ws.title ?? "",
    },
    startDate: parseApiDateOnly(b.start_date),
    endDate: parseApiDateOnly(b.end_date),
    notes: b.notes ?? "",
    worker_assignment: blockWorkers,
  };
}

/**
 * Normalize one per-day aspect API row and attach owning block id.
 *
 * @param {Object} a
 * @param {string|number} blockId
 * @returns {Object|null}
 */
export function mapAspectFromApi(a, blockId) {
  if (!a) {
    return null;
  }
  const workers = Array.isArray(a.worker_assignment) ? a.worker_assignment : [];
  return {
    id: a.id,
    dayPosition: a.day_position ?? 0,
    workScopeAspectsBlock: { id: blockId },
    date: parseApiDateOnly(a.date),
    worker_assignment: workers,
    skipped: Boolean(a.skipped),
    skipped_reason: a.skipped_reason ?? "",
    dispatch_modified: Boolean(a.dispatch_modified),
  };
}
